import React, { useEffect, useMemo, useState, createElement } from 'react';
import { subscribeToNewsletterSignups, type NewsletterSignup } from '../../../utils/firebase';
import { Search, Mail, Download, Copy, Check } from 'lucide-react';
function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}
function formatSource(source: string): string {
  if (!source || source === 'unknown') return 'Unknown';
  return source.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
export function AdminNewsletter() {
  const [signups, setSignups] = useState<NewsletterSignup[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  useEffect(() => {
    // A blocked Firestore read can hang rather than error, which would leave
    // this view spinning forever. Fall through to the empty state instead.
    const timer = setTimeout(() => {
      setLoading(false);
      setLoadFailed(true);
    }, 10000);
    const unsub = subscribeToNewsletterSignups(data => {
      clearTimeout(timer);
      setSignups(data);
      setLoading(false);
      setLoadFailed(false);
    });
    return () => {
      clearTimeout(timer);
      unsub();
    };
  }, []);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return signups;
    return signups.filter(s => s.email.toLowerCase().includes(term) || formatSource(s.source).toLowerCase().includes(term));
  }, [signups, search]);
  const handleCopyAll = async () => {
    const emails = filtered.map(s => s.email).join(', ');
    try {
      await navigator.clipboard.writeText(emails);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — no-op */
    }
  };
  const handleExportCsv = () => {
    const header = 'email,source,signed_up\n';
    const rows = filtered.map(s => `${s.email},${(s.source || 'unknown').replace(/,/g, ' ')},${s.createdAt}`).join('\n');
    const blob = new Blob([header + rows], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'newsletter-signups.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  if (loading) {
    return <div className="p-8 flex justify-center" data-id="element-250" data-mp="32ffkyn5olanm">
        <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-251" data-mp="3791ysinkzkl0" />
      </div>;
  }
  return <div className="p-8 max-w-7xl mx-auto" data-id="element-252" data-mp="29qid7dz3iw94">
      <h1 className="text-2xl font-bold text-[#111111] mb-2" data-id="element-253" data-mp="2eqwca6ipyvv0">
        Newsletter Signups
      </h1>
      <p className="text-sm text-[#5A5A5A] mb-8" data-id="element-254" data-mp="vwuwdfmtkkxt">
        Every email collected from newsletter forms across the site.
      </p>

      {loadFailed && <div role="status" className="mb-8 bg-white border border-[#E5E5E5] border-l-4 border-l-[#D97706] rounded-lg p-4" data-id="element-255" data-mp="rsvg8y5gbo4z">
          <p className="text-sm font-bold text-[#111111] mb-1" data-id="element-256" data-mp="hvqtox7p1qnn">
            Live subscriber data unavailable
          </p>
          <p className="text-sm text-[#5A5A5A]" data-id="element-257" data-mp="cza3zykuj48g">
            Signups could not be read from Firestore. This usually means you are
            not signed in as an admin, or security rules are blocking access to
            the newsletter collection.
          </p>
        </div>}

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10" data-id="element-258" data-mp="3vdjfwsl8mb4t">
        <div className="bg-white p-6 rounded-xl border border-[#E5E5E5] shadow-sm" data-id="element-259" data-mp="3qbaldrspv10l">
          <p className="text-sm font-medium text-[#5A5A5A] mb-1" data-id="element-260" data-mp="3lek3cyxcpvcj">
            Total Subscribers
          </p>
          <p className="text-3xl font-bold text-[#111111]" data-id="element-261" data-mp="1pbp3hbaxpuab">{signups.length}</p>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-xl shadow-sm overflow-hidden" data-id="element-262" data-mp="1u85vwhp62ut7">
        {/* Toolbar */}
        <div className="p-4 border-b border-[#E5E5E5] flex flex-col sm:flex-row gap-4 justify-between items-center bg-[#FAFAFA]" data-id="element-263" data-mp="1fkb1mwvigie0">
          <div className="relative w-full sm:w-96" data-id="element-264" data-mp="16k62tb4dusbg">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-265" data-mp="16ibinz0xr48f" />
            <input type="text" placeholder="Search emails or source..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white border border-[#E5E5E5] rounded-md text-sm focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate" data-id="element-266" data-mp="1bkkbmvdt66aj" />
          </div>
          <div className="flex gap-3 w-full sm:w-auto" data-id="element-267" data-mp="2vxj8p4s58oeo">
            <button type="button" onClick={handleCopyAll} disabled={filtered.length === 0} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-[#E5E5E5] rounded-md text-sm font-medium text-[#111111] hover:border-tealslate hover:text-tealslate disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-268" data-mp="kb7nl9cua1fs">
              {copied ? <>
                  <Check className="w-4 h-4" data-id="element-269" data-mp="1305mekj3engw" /> Copied
                </> : <>
                  <Copy className="w-4 h-4" data-id="element-270" data-mp="z2g8x69pl0n4" /> Copy emails
                </>}
            </button>
            <button type="button" onClick={handleExportCsv} disabled={filtered.length === 0} className="inline-flex items-center gap-2 px-3 py-2 bg-tealslate text-white rounded-md text-sm font-medium hover:bg-navyslate disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-271" data-mp="1cxsfqc152hke">
              <Download className="w-4 h-4" data-id="element-272" data-mp="1dqlgiwtq6jbl" /> Export CSV
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-273" data-mp="1iqhas4cmewdy">
          <table className="w-full text-left border-collapse" data-id="element-274" data-mp="1mpgnwc8i9zgh">
            <thead data-id="element-275" data-mp="1msb9i7399qbh">
              <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]" data-id="element-276" data-mp="1rotbiz77bak4">
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-277" data-mp="1rmyo9e8gj6cv">
                  Email
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-278" data-mp="21mfsrsbz3e79">
                  Source
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-279" data-mp="26g27dq1p5s5p">
                  Signed Up
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E5E5]" data-id="element-280" data-mp="2fg6zyu28elzt">
              {filtered.length === 0 ? <tr data-id="element-281" data-mp="2ke083sn3hq2q">
                  <td colSpan={3} className="px-6 py-12 text-center text-[#5A5A5A]" data-id="element-282" data-mp="2kbdzdpogx4ix">
                    {signups.length === 0 ? 'No newsletter signups yet.' : 'No signups match your search.'}
                  </td>
                </tr> : filtered.map(s => <tr key={s.email} className="hover:bg-[#FAFAFA] transition-colors" data-id="element-283" data-mp="2gk8vaqakms16">
                    <td className="px-6 py-4" data-id="element-284" data-mp="38a3iagayi5mo">
                      <div className="flex items-center gap-2" data-id="element-285" data-mp="38bem2g4ejdds">
                        <Mail className="w-4 h-4 text-[#9A9A90] shrink-0" data-id="element-286" data-mp="33h0xawk8w4i3" />
                        <span className="text-sm font-medium text-[#111111] break-all" data-id="element-287" data-mp="2zhjr20wzv3bv">
                          {s.email}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-288" data-mp="3r6vbbhgavz3x">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F0F4F8] text-tealslate" data-id="element-289" data-mp="3r63e62721tbj">
                        {formatSource(s.source)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#5A5A5A]" data-id="element-290" data-mp="4t65nmbn7n3j">
                      {formatDate(s.createdAt)}
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}